import React from "react";
import {
  Box,
  LinearProgress,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import type { NumberRangeOverviewRow } from "../shared/numberRangeOverview";
import ExpectedCodeBandsHint, { EVENT_CODE_RANGE_LABEL } from "./ExpectedCodeBandsHint";
import {
  normalizeToRisingEventCode,
  parseEventCodeDigitStructure,
  parseRisingCodeBandDigits,
  subDigitRangeLabel,
} from "../shared/eventCodeRange";

export type BaseFunctionGroup = {
  baseId: number;
  baseDigit: number;
  baseLabel: string;
  subs: NumberRangeOverviewRow[];
};

const MONO = {
  fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
  fontSize: "0.7rem",
} as const;

type SubBandTileProps = {
  row: NumberRangeOverviewRow;
  focused: boolean;
  highlighted: boolean;
  showFill: boolean;
  tileRef?: (el: HTMLDivElement | null) => void;
};

function SubBandTile({ row, focused, highlighted, showFill, tileRef }: SubBandTileProps) {
  const active = focused || highlighted;
  return (
    <Paper
      ref={tileRef}
      variant="outlined"
      sx={{
        p: 1.25,
        height: "100%",
        borderRadius: 1.5,
        borderWidth: active ? 2 : 1,
        borderColor: focused ? "warning.main" : "secondary.main",
        bgcolor: active ? "action.hover" : "background.paper",
        transition: "border-color 0.15s, box-shadow 0.15s",
        boxShadow: focused ? 2 : 0,
      }}
    >
      <Stack spacing={0.75} sx={{ height: "100%" }}>
        <Stack direction="row" spacing={0.75} alignItems="flex-start">
          <Box
            title={`Hundreds digit (sub function): ${row.subDigit}`}
            sx={{
              ...MONO,
              flexShrink: 0,
              width: 28,
              height: 28,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: 0.75,
              border: 1,
              borderColor: "secondary.main",
              bgcolor: "action.selected",
              fontWeight: 700,
              color: "secondary.main",
              cursor: "help",
            }}
          >
            {row.subDigit}
          </Box>
          <Typography
            variant="body2"
            fontWeight={active ? 700 : 600}
            sx={{
              lineHeight: 1.25,
              flex: 1,
              minWidth: 0,
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
            title={row.subLabel}
          >
            {row.subLabel}
          </Typography>
        </Stack>
        {showFill && row.percentFilled != null && (
          <Box sx={{ mt: "auto", pt: 0.5 }}>
            <LinearProgress
              variant="determinate"
              value={Math.min(100, Math.max(0, row.percentFilled))}
              sx={{ height: 4, borderRadius: 2 }}
            />
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.65rem" }}>
              {row.percentFilled.toFixed(0)}% used
            </Typography>
          </Box>
        )}
      </Stack>
    </Paper>
  );
}

function BaseSectionHeader({
  baseLabel,
  baseDigit,
  catalogSubDigits,
}: {
  baseLabel: string;
  baseDigit: number;
  catalogSubDigits: number[];
}) {
  return (
    <Stack
      direction={{ xs: "column", sm: "row" }}
      spacing={{ xs: 1, sm: 2 }}
      alignItems={{ xs: "stretch", sm: "flex-start" }}
      justifyContent="space-between"
      sx={{ mb: 1.5 }}
    >
      <Typography variant="subtitle1" fontWeight={700} sx={{ flex: 1, minWidth: 0 }}>
        {baseLabel}
      </Typography>
      <Box sx={{ textAlign: { xs: "left", sm: "right" }, flexShrink: 0 }}>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.35 }}>
          {EVENT_CODE_RANGE_LABEL}
        </Typography>
        <ExpectedCodeBandsHint
          baseDigit={baseDigit}
          catalogSubDigits={catalogSubDigits}
          layout="inline"
          sx={{ fontSize: "0.75rem", lineHeight: 1.45 }}
        />
      </Box>
    </Stack>
  );
}

type DigitCellRole = "band" | "base" | "sub" | "slot";

type DigitCellDef = {
  label: string;
  caption: string;
  title: string;
  role: DigitCellRole;
};

function digitCellWidth(role: DigitCellRole, label?: string): number {
  if (role === "band") return 34;
  if (role === "sub") {
    const len = label?.length ?? 3;
    return Math.max(28, Math.min(48, len * 8 + 12));
  }
  return 28;
}

function digitCellBorderColor(role: DigitCellRole): string {
  if (role === "band") return "info.main";
  if (role === "base") return "primary.main";
  if (role === "sub") return "secondary.main";
  return "divider";
}

const CAPTION_SX = {
  fontSize: "0.6rem",
  lineHeight: 1.15,
  textAlign: "center",
  width: "100%",
} as const;

function DigitCell({ cell }: { cell: DigitCellDef }) {
  const w = digitCellWidth(cell.role, cell.label);
  return (
    <Stack spacing={0.35} alignItems="center" sx={{ width: w }}>
      <Box
        title={cell.title}
        sx={{
          ...MONO,
          width: w,
          height: 28,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          borderRadius: 0.75,
          border: 1,
          borderColor: digitCellBorderColor(cell.role),
          bgcolor: cell.role === "slot" ? "action.hover" : "action.selected",
          fontWeight: cell.role === "slot" ? 400 : 700,
          color: "text.primary",
          cursor: "help",
        }}
      >
        {cell.label}
      </Box>
      <Typography variant="caption" color="text.secondary" sx={CAPTION_SX}>
        {cell.caption}
      </Typography>
    </Stack>
  );
}

function DigitStrip({
  baseDigit,
  catalogSubDigits,
  fillFromEventCode,
}: {
  baseDigit: number;
  catalogSubDigits: number[];
  fillFromEventCode?: number | null;
}) {
  const filled =
    fillFromEventCode != null &&
    parseRisingCodeBandDigits(normalizeToRisingEventCode(fillFromEventCode)).baseDigit === baseDigit
      ? parseEventCodeDigitStructure(fillFromEventCode)
      : null;

  const subRangeLabel = subDigitRangeLabel(catalogSubDigits);
  const subRangeTitle =
    catalogSubDigits.length === 0
      ? "No sub functions in catalog"
      : catalogSubDigits.length === 1
        ? `Sub hundreds digit ${subRangeLabel} (only sub in catalog)`
        : `Sub hundreds digits ${subRangeLabel} (catalog range)`;

  const leadingCells: DigitCellDef[] = filled
    ? [
        {
          label: filled.bandDigit,
          caption: filled.bandDigit === "1" ? "↓" : "↑",
          title:
            filled.bandDigit === "1"
              ? "Resolving (1xxxx)"
              : "Rising (0xxxx)",
          role: "band",
        },
        {
          label: String(filled.baseDigit),
          caption: "base",
          title: "Base (thousands digit)",
          role: "base",
        },
        {
          label: String(filled.subDigit),
          caption: "sub",
          title: `Sub hundreds digit ${filled.subDigit}`,
          role: "sub",
        },
      ]
    : [
        {
          label: "0/1",
          caption: "↑ ↓",
          title: "Rising (0xxxx) or resolving (1xxxx, +10000)",
          role: "band",
        },
        {
          label: String(baseDigit),
          caption: "base",
          title: "Base (thousands digit)",
          role: "base",
        },
        {
          label: subRangeLabel,
          caption: "sub",
          title: subRangeTitle,
          role: "sub",
        },
      ];

  const sequenceTitle = filled
    ? `Serial number ${filled.serialTens}${filled.serialUnits} (from lookup)`
    : "Last two digits: sequential event number within the sub band (1–99, up to 99 codes per sub)";

  const serialTensLabel = filled ? String(filled.serialTens) : "n";
  const serialUnitsLabel = filled ? String(filled.serialUnits) : "n";

  return (
    <Stack
      direction="row"
      spacing={0.5}
      alignItems="flex-start"
      flexWrap="wrap"
      useFlexGap
      sx={{
        mb: 1.5,
        ...(filled
          ? {
              px: 0.75,
              py: 0.5,
              mx: -0.75,
              borderRadius: 1,
              bgcolor: "action.selected",
            }
          : {}),
      }}
    >
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ mr: 0.25, minWidth: 44, pt: 0.75, lineHeight: 1.2 }}
      >
        5-digit
      </Typography>
      {leadingCells.map((cell) => (
        <DigitCell key={cell.role} cell={cell} />
      ))}
      <Stack spacing={0.35} alignItems="center" sx={{ minWidth: 72 }}>
        <Box
          title={sequenceTitle}
          sx={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            height: 28,
            px: 0.75,
            gap: 0.35,
            borderRadius: 0.75,
            border: 1,
            borderColor: digitCellBorderColor("slot"),
            bgcolor: "action.hover",
            cursor: "help",
          }}
        >
          <Box
            sx={{
              ...MONO,
              width: 22,
              height: 22,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: 0.5,
              border: 1,
              borderColor: "divider",
              bgcolor: "background.paper",
              fontWeight: 600,
              fontSize: "0.7rem",
            }}
          >
            {serialTensLabel}
          </Box>
          {!filled && (
            <Typography
              component="span"
              sx={{
                ...MONO,
                fontSize: "0.62rem",
                color: "text.secondary",
                lineHeight: 1,
                letterSpacing: -0.5,
              }}
            >
              …
            </Typography>
          )}
          <Box
            sx={{
              ...MONO,
              width: 22,
              height: 22,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: 0.5,
              border: 1,
              borderColor: "divider",
              bgcolor: "background.paper",
              fontWeight: 600,
              fontSize: "0.7rem",
            }}
          >
            {serialUnitsLabel}
          </Box>
          {!filled && (
            <>
              <Typography
                component="span"
                sx={{
                  fontSize: "0.55rem",
                  color: "text.disabled",
                  lineHeight: 1,
                  mx: 0.1,
                }}
              >
                →
              </Typography>
              <Typography
                component="span"
                sx={{
                  ...MONO,
                  fontSize: "0.65rem",
                  fontWeight: 700,
                  color: "text.secondary",
                  whiteSpace: "nowrap",
                }}
              >
                1–99
              </Typography>
            </>
          )}
        </Box>
        <Typography variant="caption" color="text.secondary" sx={CAPTION_SX}>
          serial no.
        </Typography>
      </Stack>
    </Stack>
  );
}

export type EventCodeStructureMapProps = {
  groups: BaseFunctionGroup[];
  highlightBaseId?: number | null;
  highlightSubId?: number | null;
  focusTarget?: { baseId: number; subId?: number } | null;
  /** When set (e.g. after code lookup), fills the 5-digit strip for the matching base. */
  fillFromEventCode?: number | null;
  showFillLevels?: boolean;
  baseSectionRefs: React.MutableRefObject<Map<number, HTMLElement>>;
  tileRefs: React.MutableRefObject<Map<string, HTMLDivElement>>;
};

export default function EventCodeStructureMap({
  groups,
  highlightBaseId,
  highlightSubId,
  focusTarget,
  fillFromEventCode = null,
  showFillLevels = false,
  baseSectionRefs,
  tileRefs,
}: EventCodeStructureMapProps) {
  if (groups.length === 0) {
    return (
      <Paper variant="outlined" sx={{ py: 4, textAlign: "center" }}>
        <Typography variant="body2" color="text.secondary">
          No functions to display.
        </Typography>
      </Paper>
    );
  }

  return (
    <Stack spacing={2}>
      {groups.map((group) => {
        const baseHighlighted = highlightBaseId != null && group.baseId === highlightBaseId;
        return (
          <Paper
            key={group.baseId}
            ref={(el) => {
              if (el) baseSectionRefs.current.set(group.baseId, el);
              else baseSectionRefs.current.delete(group.baseId);
            }}
            elevation={2}
            sx={{
              p: { xs: 1.5, sm: 2 },
              borderRadius: 2,
              borderWidth: baseHighlighted ? 2 : 0,
              borderStyle: "solid",
              borderColor: "primary.main",
            }}
          >
            <BaseSectionHeader
              baseLabel={group.baseLabel}
              baseDigit={group.baseDigit}
              catalogSubDigits={group.subs.map((s) => s.subDigit)}
            />

            <DigitStrip
              baseDigit={group.baseDigit}
              catalogSubDigits={group.subs.map((s) => s.subDigit)}
              fillFromEventCode={fillFromEventCode}
            />

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))",
                gap: 1.25,
              }}
            >
              {group.subs.map((row) => {
                const tileKey = `${row.baseId}-${row.subId}`;
                const focused =
                  focusTarget?.baseId === row.baseId && focusTarget?.subId === row.subId;
                const highlighted =
                  (highlightBaseId != null && row.baseId === highlightBaseId &&
                    (highlightSubId == null || row.subId === highlightSubId)) ||
                  false;
                return (
                  <SubBandTile
                    key={tileKey}
                    row={row}
                    focused={focused}
                    highlighted={highlighted && !focused}
                    showFill={showFillLevels}
                    tileRef={(el) => {
                      if (el) tileRefs.current.set(tileKey, el);
                      else tileRefs.current.delete(tileKey);
                    }}
                  />
                );
              })}
            </Box>
          </Paper>
        );
      })}
    </Stack>
  );
}
